/**
 * Session Monitor
 * 
 * This monitors user session validity in the background and redirects users to the login page when their session expires. 
 * It works across both React pages and traditional Rails views.
 * 
 * HOW IT WORKS:
 * 1. Tracks user activity (mouse, keyboard, scroll, touch events)
 * 2. Only checks session when user has been inactive for 5+ minutes
 * 3. Polls the session check API every 15 minutes during inactivity
 * 4. Session check does NOT update accessed_at or refresh the cookie
 * 5. Session expires after 8 hours (production) or 1 week (local) of true inactivity
 * 6. Redirects to login if session is invalid
 * 
 * IMPORTANT: The session check endpoint skips the refresh_cookie callback,
 * so polling does NOT extend the session. Only actual user actions (requests) do:
 * - User actively clicking/navigating = session stays alive via normal requests
 * - User idle with tab open for 8+ hours = session expires, redirected on next poll
 * - User closes browser = session expires after 8 hours
 * 
 * USAGE:
 * - For React pages: Import and call sessionMonitor.init() in your app entry point
 * - For Rails views: Automatically initializes on DOMContentLoaded
 */

// Time between session validity checks (15 minutes in production, 10 seconds for testing)
// Switch as needed

// const SESSION_CHECK_INTERVAL = 10 * 1000; // 10 seconds for testing
const SESSION_CHECK_INTERVAL = 15 * 60 * 1000; // 15 minutes (production)

// This prevents unnecessary API calls when the user is actively using the app
// Minimum idle time before we start checking (5 minutes in production, 2 seconds for testing)
// Switch as needed

// const INACTIVITY_THRESHOLD = 2 * 1000; // 2 seconds for testing
const INACTIVITY_THRESHOLD = 5 * 60 * 1000; // 5 minutes (production)

class SessionMonitor {
  constructor() {
    // ID for the interval timer (so we can clear it later)
    this.intervalId = null;
    
    // Timestamp of the last user activity
    this.lastActivityTime = Date.now();
    
    // Flag to prevent multiple initializations
    this.isInitialized = false;
  }

  /**
   * Initialize the session monitor
   * Safe to call multiple times - will only initialize once
   */
  init() {
    // Guard against multiple initializations
    if (this.isInitialized) {
      console.log('[SessionMonitor] Already initialized, skipping');
      return;
    }
    
    console.log('[SessionMonitor] Initializing...');
    this.isInitialized = true;
    
    // Set up event listeners to track user activity
    this.trackUserActivity();
    
    // Set up listener for when user returns to the tab
    this.trackPageVisibility();
    
    // Set up listener for Turbo navigation (Rails views)
    this.trackTurboNavigation();
    
    // Start the periodic session checking
    this.startMonitoring();
    
    console.log(`[SessionMonitor] Initialized! Check interval: ${SESSION_CHECK_INTERVAL/1000}s, Inactivity threshold: ${INACTIVITY_THRESHOLD/1000}s`);
  }

  /**
   * Track user activity events to determine if user is active or idle
   * Updates lastActivityTime whenever user interacts with the page
   */
  trackUserActivity() {
    // Events that indicate the user is actively using the page
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    
    const updateActivity = () => {
      this.lastActivityTime = Date.now();
    };
    
    // Add listeners for all activity events
    // passive: true improves scroll performance
    events.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });
  }

  /**
   * Check session when user returns to the tab
   * Uses the Page Visibility API to detect when the tab becomes active
   */
  trackPageVisibility() {
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        // User returned to tab - check session immediately
        // They may have been away for a long time
        console.log('[SessionMonitor] Tab became visible, checking session');
        this.checkSession();
      }
    });
  }

  /**
   * Track Turbo navigation for Rails views
   * Turbo Drive handles page transitions without full page reloads
   * We update activity time on navigation to indicate the user is active
   */
  trackTurboNavigation() {
    document.addEventListener('turbo:load', () => {
      console.log('[SessionMonitor] Turbo navigation detected');
      this.lastActivityTime = Date.now();
    });
  }

  /**
   * Start the periodic session checking interval
   * Checks session immediately, then every 15 minutes
   */
  startMonitoring() {
    // Check immediately on init
    this.checkSession();
    
    // Then check periodically every SESSION_CHECK_INTERVAL (15 minutes)
    this.intervalId = setInterval(() => {
      this.checkSession();
    }, SESSION_CHECK_INTERVAL);
  }

  /**
   * Check if the user's session is still valid
   * Only performs the check if user has been inactive for 5+ minutes
   * This prevents unnecessary API calls during active usage
   */
  async checkSession() {
    // Calculate how long the user has been inactive
    const inactiveDuration = Date.now() - this.lastActivityTime;
    
    // Skip check if user has been active recently
    // No need to check session if they're actively using the app
    if (inactiveDuration < INACTIVITY_THRESHOLD) {
      console.log(`[SessionMonitor] User active (${Math.round(inactiveDuration/1000)}s ago), skipping check`);
      return;
    }

    console.log('[SessionMonitor] Checking session validity...');

    try {
      // Call the session check API endpoint
      const response = await fetch('/api/session_check', {
        method: 'GET',
        credentials: 'same-origin', // Include cookies for authentication
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      const data = await response.json();
      
      if (!data.valid) {
        // Session is expired or invalid
        console.log('[SessionMonitor] Session expired!');
        this.handleSessionExpired();
      } else {
        // Session is still valid
        console.log('[SessionMonitor] Session valid');
      }
    } catch (error) {
      // Network error or API failure
      // DON'T log the user out - it could be a temporary network issue
      // They'll discover the real problem when they try to perform an action
      console.error('[SessionMonitor] Check failed:', error);
    }
  }

  /**
   * Handle session expiration
   * Stop monitoring and redirect to login with a timeout message
   * Passes the current location as a URL parameter
   */
  handleSessionExpired() {
    // Stop the interval timer
    this.stopMonitoring();
    
    // Pass current location as URL parameter for redirect after login
    const currentPath = window.location.pathname + window.location.search;
    const returnTo = encodeURIComponent(currentPath);
    console.log('[SessionMonitor] Redirecting to login with return_to:', currentPath);
    
    // Redirect to login page with timeout message and return_to parameter
    window.location.href = `/login?msg=session_timeout&return_to=${returnTo}`;
  }

  /**
   * Stop the periodic session checking
   * Clears the interval timer
   */
  stopMonitoring() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * Destroy the session monitor
   * Stops monitoring and resets the initialization flag
   * Useful for cleanup in tests
   */
  destroy() {
    this.stopMonitoring();
    this.isInitialized = false;
  }
}

// Create and export a singleton instance
// This ensures we only have one monitor running across the entire application
const sessionMonitor = new SessionMonitor();
export default sessionMonitor;

// AUTO-INITIALIZATION FOR RAILS VIEWS
// React pages will call init() explicitly, but Rails ERB views need automatic initialization
if (typeof document !== 'undefined') {
  const initOnReady = () => {
    // Don't initialize on login or password reset pages - no active session to monitor
    const isUnauthenticatedPage = window.location.pathname.includes('/login') || 
                                  window.location.pathname.includes('/passwords');
    
    if (!isUnauthenticatedPage) {
      sessionMonitor.init();
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initOnReady);
  } else {
    // DOM already loaded (module loaded late)
    initOnReady();
  }
}
